import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  BloomLogo,
  Button,
  cn,
  LocaleSwitcher,
  SearchCommand,
  CommandItem,
  CommandGroup,
  CommandEmpty,
} from "@bloom/ui";
import { Search } from "lucide-react";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { api } from "../api/client.ts";

interface Suggestion {
  id: string;
  slug: string;
  titleTh: string;
  titleEn: string;
}

const SCROLL_TRIGGER_PX = 80;

export function TopNav() {
  const { locale, setLocale, t } = useLocale();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Suggestion[]>([]);
  const [scrolled, setScrolled] = useState(false);

  // On the landing route the nav floats on top of the pink hero; once the
  // user scrolls past the hero (or on any non-home route) we switch to a
  // frosted-glass surface so links stay legible over content underneath.
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_TRIGGER_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset scrolled-state immediately on route change so the new route gets
  // its first paint with the correct background (don't flash transparent on
  // a deep route).
  useEffect(() => {
    setScrolled(window.scrollY > SCROLL_TRIGGER_PX);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!searchOpen || !query) {
      setHits([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await api.get<{ hits: Suggestion[] }>(
          `/api/search/suggest?q=${encodeURIComponent(query)}`,
        );
        setHits(data.hits);
      } catch {
        setHits([]);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [query, searchOpen]);

  const transparentTop = onLanding && !scrolled;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        transparentTop
          ? "bg-transparent"
          : "border-b border-black/5 bg-white/70 shadow-sm backdrop-blur-xl backdrop-saturate-150",
      )}
    >
      <nav
        className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
        aria-label="Primary"
      >
        {/* LEFT CLUSTER — logo + primary nav, hugging the left edge */}
        <div className="flex items-center gap-6 lg:gap-10">
          <Link to="/" aria-label="Bloom home">
            <BloomLogo className="h-7 text-[var(--brand-ink)]" />
          </Link>
          <ul className="hidden items-center gap-6 text-sm font-medium text-[var(--brand-ink)] md:flex lg:gap-7">
            <li>
              <Link to="/" className="transition-opacity hover:opacity-70">
                {t("nav.home")}
              </Link>
            </li>
            <li>
              <Link to="/series" className="transition-opacity hover:opacity-70">
                {t("nav.all_series")}
              </Link>
            </li>
            <li>
              <Link to="/series?sort=newest" className="transition-opacity hover:opacity-70">
                {t("nav.new")}
              </Link>
            </li>
            <li>
              <Link to="/about" className="transition-opacity hover:opacity-70">
                {t("nav.about")}
              </Link>
            </li>
          </ul>
        </div>

        {/* RIGHT CLUSTER — controls + auth, hugging the right edge */}
        <div className="flex items-center gap-3 text-[var(--brand-ink)] sm:gap-4">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label={t("nav.search")}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-black/5"
          >
            <Search className="h-5 w-5" />
          </button>
          <LocaleSwitcher current={locale} onChange={setLocale} className="hidden sm:inline-flex" />
          {user ? (
            <Link
              to="/account"
              className="rounded-full bg-white/40 px-3 py-1.5 text-sm font-semibold backdrop-blur"
            >
              {user.name ?? user.email.split("@")[0]}
            </Link>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="hidden text-sm font-medium transition-opacity hover:opacity-70 sm:inline-flex"
              >
                {t("nav.login")}
              </button>
              <Button variant="primary" size="sm" onClick={() => navigate("/signup")}>
                {t("nav.signup")}
              </Button>
            </>
          )}
        </div>
      </nav>

      <SearchCommand
        open={searchOpen}
        onOpenChange={setSearchOpen}
        placeholder={t("nav.search")}
        value={query}
        onValueChange={setQuery}
      >
        <CommandGroup heading={t("landing.trending.title")}>
          {hits.length === 0 ? (
            <CommandEmpty>{t("errors.not_found")}</CommandEmpty>
          ) : (
            hits.map((h) => (
              <CommandItem
                key={h.id}
                value={`${h.slug} ${h.titleEn} ${h.titleTh}`}
                onSelect={() => {
                  setSearchOpen(false);
                  navigate(`/series/${h.slug}`);
                }}
              >
                <span className="font-semibold">{locale === "th" ? h.titleTh : h.titleEn}</span>
              </CommandItem>
            ))
          )}
        </CommandGroup>
      </SearchCommand>
    </header>
  );
}
